import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, ArrowRight, Plus } from 'lucide-react';
import { CopilotThread } from '@/hooks/ai-agents/types/database-types';
import { CopilotInputBar } from '@/components/ai-agents/input-bar/CopilotInputBar';
import { Skeleton } from '@/components/ui/skeleton';
import { format, isToday, isYesterday } from 'date-fns';
import { useLocalStorageState } from '@/hooks/ai-agents/useLocalStorageState';
import { useNavigate } from 'react-router-dom';
import DashboardButton from '@/components/ui/DashboardButton';

interface QuickChatThreadsProps {
  threads: CopilotThread[];
  isLoading: boolean;
  onThreadClick: (threadId: string) => void;
  onSendMessage: (message: string) => void;
  onViewAllThreads: () => void;
  isSendingMessage?: boolean;
}

export const QuickChatThreads: React.FC<QuickChatThreadsProps> = ({
  threads,
  isLoading,
  onThreadClick,
  onSendMessage,
  onViewAllThreads,
  isSendingMessage = false
}) => {
  // Get input value and setter from localStorage state for sync with AI chat modal
  const { inputValue, setInputValue } = useLocalStorageState();
  const navigate = useNavigate();
  
  const formatDate = (dateStr: string | number) => {
    const date = typeof dateStr === 'number' ? new Date(dateStr) : new Date(dateStr);
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'MMM d');
  };

  const truncateMessage = (message: string, maxLength: number = 40) => {
    if (message.length <= maxLength) return message;
    const truncated = message.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    if (lastSpaceIndex > maxLength * 0.7) {
      return truncated.substring(0, lastSpaceIndex) + '...';
    }
    return truncated + '...';
  };

  // Show skeleton while loading
  if (isLoading) {
    return (
      <div className="space-y-4">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-16" />
        </div>
        
        {/* Thread skeletons */}
        <div className="space-y-2">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="flex items-center gap-2 p-2 rounded-md border border-gray-200 dark:border-gray-700">
              <Skeleton className="h-3 w-3 rounded-full" />
              <div className="flex-1">
                <Skeleton className="h-3 w-full mb-1" />
                <Skeleton className="h-2 w-16" />
              </div>
            </div>
          ))}
        </div>
        
        {/* Input skeleton */}
        <Skeleton className="h-16 w-full rounded-lg" />
      </div>
    );
  }

  // Show recent threads (max 4)
  const recentThreads = threads.slice(0, 4);
  const hasMoreThreads = threads.length > 4;

  return (
    <div className="space-y-4">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
          Recent Conversations
        </h3>
        <div className="flex items-center gap-2">
          <DashboardButton
            onClick={() => navigate('/account/ai-agents')}
            size="sm"
            className="h-8 px-3 py-1.5 text-sm"
          >
            Meet Your Agents
          </DashboardButton>
          {hasMoreThreads && (
            <button
              onClick={onViewAllThreads}
              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
            >
              View All
            </button>
          )}
        </div>
      </motion.div>

      {/* Recent Threads */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="space-y-3"
      >
        {recentThreads.map((thread, index) => {
          const displayMessage = thread.lastMsgFromUser 
            ? truncateMessage(thread.lastMsgFromUser)
            : 'New conversation';

          return (
            <motion.button
              key={thread.id}
              onClick={() => onThreadClick(thread.id)}
              className="group w-full p-4 text-left rounded-xl bg-gradient-to-r from-white/60 to-white/40 dark:from-slate-800/60 dark:to-slate-700/40 backdrop-blur-sm border border-white/30 dark:border-slate-600/30 shadow-sm hover:shadow-md transition-all duration-300"
              whileHover={{ 
                scale: 1.02,
                y: -2,
                transition: { 
                  duration: 0.2, 
                  ease: "easeOut"
                }
              }}
              whileTap={{ 
                scale: 0.98,
                transition: { 
                  duration: 0.1, 
                  ease: "easeInOut"
                }
              }}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
            >
              <div className="flex items-start gap-3">
                {/* Enhanced Icon */}
                <motion.div 
                  className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-sm"
                  whileHover={{ 
                    rotate: [0, -3, 3, 0],
                    scale: 1.1,
                    transition: { 
                      duration: 0.4, 
                      ease: "easeInOut"
                    }
                  }}
                >
                  <MessageSquare className="h-4 w-4 text-white" />
                </motion.div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate leading-tight">
                    {displayMessage}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 opacity-60"></div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(thread.updatedAt || thread.createdAt)}
                    </p>
                  </div>
                </div>

                {/* Arrow with enhanced animation */}
                <motion.div
                  className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  whileHover={{
                    x: 2,
                    transition: { duration: 0.2, ease: "easeOut" }
                  }}
                >
                  <ArrowRight className="h-4 w-4 text-blue-500" />
                </motion.div>
              </div>
            </motion.button>
          );
        })}
      </motion.div>

      {/* New Conversation Section */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-3"
      >
        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700"></div>
          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <Plus className="h-3 w-3" />
            <span>New</span>
          </div>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700"></div>
        </div>

        {/* Input Bar */}
        <CopilotInputBar
          onSendMessage={onSendMessage}
          isLoading={isSendingMessage}
          placeholder="Start a new conversation..."
          minRows={1}
          maxRows={3}
          value={inputValue}
          onChange={setInputValue}
        />
      </motion.div>
    </div>
  );
}; 